import React from "react";
import { CalendarDays, Package, Truck, MapPin } from "lucide-react";

/**
 * Thu 11pm → Fri → Sat cadence strip.
 * Renders as a horizontal 3-step visual on desktop, stacks on mobile.
 * `variant`:
 *   - "light" (default)  — cream background, dark text (for on-light sections)
 *   - "dark"             — ink background, light text (for hero / on-dark sections)
 *   - "compact"          — inline text with dividers (for cart / footers)
 * `mode`:
 *   - "delivery" (default) — third step is "Deliver Sat · Perth Metro"
 *   - "pickup"             — third step is "Collect Sat · Malaga"
 * `zone`:
 *   - undefined (default) — Perth metro copy (Thu 11pm → Deliver Sat)
 *   - "bunbury"           — swaps to tightened Bunbury cycle
 *                           (Thu 1pm → Pack Fri AM → Deliver Fri Morning)
 *                           Only pass this on the Cart / Checkout / Bunbury
 *                           landing — the rest of the site MUST stay on the
 *                           Perth default per the brief.
 */
export default function DeliveryCadence({
  variant = "light",
  mode = "delivery",
  zone,
  className = "",
}) {
  const isPickup = mode === "pickup";
  const isBunbury = zone === "bunbury" && !isPickup;
  const steps = [
    isBunbury
      ? { icon: CalendarDays, title: "Order by Thu 1pm", body: "Bunbury cut-off — tighter than metro" }
      : { icon: CalendarDays, title: "Order by Thu 11pm", body: "Weekly cut-off — subscribe or one-off" },
    isBunbury
      ? { icon: Package, title: "We Pack Friday AM", body: "Vacuum-sealed & labelled at our Malaga butchery" }
      : { icon: Package, title: "We Pack Friday", body: "Vacuum-sealed & labelled at our Malaga butchery" },
    isPickup
      ? { icon: MapPin, title: "Collect Sat · Malaga", body: "15 Action Road · ready within 2 hours" }
      : isBunbury
        ? { icon: Truck, title: "Deliver Fri Morning · Bunbury", body: "Cold to your door · Bunbury Region" }
        : { icon: Truck, title: "Deliver Sat · Perth Metro", body: "Cold to your door · free delivery" },
  ];

  if (variant === "compact") {
    return (
      <div
        className={`text-[10px] uppercase tracking-widest text-[color:var(--tbb-muted)] ${className}`}
        data-testid="delivery-cadence-compact"
      >
        {isBunbury
          ? "Order Thu 1pm · Pack Fri AM · Deliver Fri Morning · Bunbury Region"
          : `Order Thu 11pm · Pack Fri · ${isPickup ? "Collect Sat · Malaga" : "Deliver Sat · Perth Metro"}`}
      </div>
    );
  }

  const isDark = variant === "dark";
  const bg = isDark ? "bg-white/5" : "bg-[color:var(--tbb-cream)]/60";
  const border = isDark ? "border-white/10" : "border-[color:var(--tbb-line)]";
  const titleClr = isDark ? "text-white" : "text-[color:var(--tbb-ink)]";
  const bodyClr = isDark ? "text-white/70" : "text-[color:var(--tbb-muted)]";
  const eyebrow = isDark ? "text-white/60" : "text-[color:var(--tbb-muted)]";
  const accent = "text-[color:var(--tbb-burgundy)]";

  return (
    <div className={className} data-testid="delivery-cadence">
      <div className={`overline mb-3 ${eyebrow}`}>Every Week · One Cycle</div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {steps.map((s, i) => {
          const Icon = s.icon;
          return (
            <div
              key={s.title}
              className={`${bg} border ${border} p-5 relative`}
              data-testid={`cadence-step-${i + 1}`}
            >
              <div className="flex items-center gap-3 mb-2">
                <span className={`font-display text-2xl ${accent} leading-none`}>0{i + 1}</span>
                <Icon size={18} className={accent} />
              </div>
              <div className={`font-display text-lg uppercase tracking-tight leading-tight ${titleClr}`}>
                {s.title}
              </div>
              <div className={`text-[12px] mt-1 leading-relaxed ${bodyClr}`}>{s.body}</div>
              {/* Arrow between steps on desktop */}
              {i < steps.length - 1 && (
                <div
                  className={`hidden md:block absolute right-[-10px] top-1/2 -translate-y-1/2 z-10 ${accent} font-black`}
                  aria-hidden="true"
                >
                  ›
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
